import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Check,
  Loader2,
  UserX,
  Zap,
  Bell,
  FileText,
} from 'lucide-react';
import { billingApi } from '@/lib/api';
import { getPlanById } from '@/data/pricingPlans';
import { useBusinessNav } from '@/hooks/useBusinessNav';

interface SubscriptionCancelledModalProps {
  businessId: string;
  onClose: () => void;
}

const features = [
  {
    icon: Zap,
    title: 'Unlimited invoices & quotes',
    description: 'Send as many documents as your business needs.',
  },
  {
    icon: Bell,
    title: 'Automated payment reminders',
    description: 'Get paid faster with smart follow-ups.',
  },
  {
    icon: FileText,
    title: 'Premium document designs',
    description: 'Professional templates that impress clients.',
  },
];

const includedFeatures = [
  'Accept credit card & ACH payments',
  'Client management & history',
  'Read receipts & tracking',
  'iOS & Android apps',
  'Unlimited clients',
  'Email support',
];

const animationConfig = {
  backdrop: { bg: 'rgba(0, 0, 0, 0.04)', blur: '8px' },
  duration: 400,
};

export function SubscriptionCancelledModal({ businessId, onClose }: SubscriptionCancelledModalProps) {
  const { nav } = useBusinessNav();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState<'entering' | 'visible' | 'exiting' | 'hidden'>('hidden');

  const premiumPlan = getPlanById('premium');
  const isAnnual = billingCycle === 'annual';
  const monthlyPrice = premiumPlan?.pricing.monthly ?? 0;
  const annualPrice = premiumPlan?.pricing.annually ?? 0;
  const price = isAnnual ? annualPrice : monthlyPrice;
  const savingsPercent = monthlyPrice > 0 ? Math.round((1 - annualPrice / monthlyPrice) * 100) : 0;
  const config = animationConfig;

  useEffect(() => {
    setPhase('entering');
    const timer = setTimeout(() => setPhase('visible'), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleResubscribe = async () => {
    setLoading(true);
    const response = await billingApi.createCheckoutSession(
      'premium',
      isAnnual ? 'annual' : 'monthly',
      businessId
    );
    if (response.data?.checkout_url) {
      window.location.href = response.data.checkout_url;
    } else {
      setLoading(false);
    }
  };

  const showContent = phase !== 'hidden';
  if (!showContent && phase === 'hidden') return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop - no click to close for cancelled subscription */}
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: phase === 'visible' ? config.backdrop.bg : 'rgba(0, 0, 0, 0)',
          backdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
          WebkitBackdropFilter: phase === 'visible' ? `blur(${config.backdrop.blur})` : 'blur(0px)',
          transition: `background-color ${config.duration}ms ease-out, backdrop-filter ${config.duration}ms ease-out`,
        }}
      />

      {/* Modal */}
      <div
        className="relative w-full max-w-3xl"
        style={{
          opacity: phase === 'visible' ? 1 : 0,
          transform: phase === 'visible' ? 'scale(1)' : 'scale(0.98)',
          transition: `opacity ${config.duration}ms ease-out, transform ${config.duration}ms ease-out`,
        }}
      >
        <div className="relative bg-white dark:bg-zinc-900 rounded-[32px] shadow-2xl overflow-hidden">
          <div className="grid md:grid-cols-2">
            {/* Left side - Message */}
            <div className="px-8 pt-8 pb-6 md:px-10 md:pt-10 md:pb-8 bg-zinc-50 dark:bg-zinc-800/50 flex flex-col">
              <div className="mb-8">
                <div className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 dark:text-red-400 mb-2">
                  <UserX className="h-4 w-4" />
                  Subscription ended
                </div>
                <h2 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                  Welcome back
                </h2>
                <p className="text-zinc-600 dark:text-zinc-400 mt-2">
                  Your subscription has ended, but your data is exactly where you left it. Subscribe to pick up right where you left off.
                </p>
              </div>

              <div className="space-y-5">
                {features.map((feature) => (
                  <div key={feature.title} className="flex gap-4">
                    <div className="h-10 w-10 rounded-lg bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="h-5 w-5 text-zinc-700 dark:text-zinc-300" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-zinc-900 dark:text-zinc-100 text-sm">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 md:mt-auto mb-2 pt-5 border-t border-zinc-200 dark:border-zinc-700">
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  All your invoices, quotes, clients, and payment history are preserved. Nothing was deleted.
                </p>
              </div>
            </div>

            {/* Right side - Pricing */}
            <div className="px-8 pt-8 pb-6 md:px-10 md:pt-10 md:pb-8 flex flex-col">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
                  Premium Plan
                </h3>

                {/* Billing toggle */}
                <Tabs value={billingCycle} onValueChange={(v) => setBillingCycle(v as 'monthly' | 'annual')}>
                  <TabsList className="h-10 w-full">
                    <TabsTrigger value="monthly" className="flex-1">Monthly</TabsTrigger>
                    <TabsTrigger value="annual" className="flex-1">
                      Annual
                      <Badge variant="success" className="ml-1.5 text-[10px] px-1.5 py-0">
                        SAVE {savingsPercent}%
                      </Badge>
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center">
                  <span className="text-5xl font-bold text-zinc-900 dark:text-zinc-100 tracking-tight">
                    ${price}
                  </span>
                  <span className="text-zinc-500 dark:text-zinc-400 ml-1">/mo</span>
                </div>
                <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                  {isAnnual ? `$${price * 12} billed annually` : 'billed monthly'}
                </p>
              </div>

              {/* Included features */}
              <div className="mb-6 flex-1">
                <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wide mb-3">
                  Everything included
                </p>
                <div className="grid grid-cols-1 gap-2">
                  {includedFeatures.map((feature) => (
                    <div key={feature} className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-zinc-600 dark:text-zinc-400">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="mt-auto pt-6">
                <Button
                  onClick={handleResubscribe}
                  disabled={loading}
                  className="w-full h-11"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Choose a Plan'
                  )}
                </Button>

                <button
                  onClick={() => {
                    onClose();
                    nav('/settings/billing');
                  }}
                  className="w-full mt-1 py-1.5 text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-300 transition-colors"
                >
                  View all plans
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
